import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Molecules/Alert/Alert';
import Button from 'Components/Atoms/Button/Button';
import OutlineDocs from 'Components/Atoms/OutlineIcons/OutlineDocs';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import useConnectorsIncidents from 'Hooks/useConnectorsIncidents';
import useConnectorOutageTexts from 'Hooks/useConnectorOutageTexts';
import { CONNECTOR_HEALTH_STATUSES } from 'Utils/constants';
import storage from 'Utils/storage';

const DISMISSED_STORAGE_KEY = 'bt-bank-outages-dismissed';

const getIncidentKey = (incident) => `${incident.connector_id}:${incident.health_status_since?.toISOString() ?? ''}`;

const readDismissed = () => {
    try {
        return JSON.parse(storage.getItem(DISMISSED_STORAGE_KEY) ?? '[]');
    } catch {
        return [];
    }
};

export default function AppLayoutNoticesBankOutages() {
    const queryClient = useQueryClient();
    const { getOutageTexts, helpLabel, helpLink } = useConnectorOutageTexts();

    const { validProvidersConnections } = useProvidersConnections();
    const { incidents, isSuccess } = useConnectorsIncidents();
    const [dismissed, setDismissed] = useState(readDismissed);

    // When a connector goes down or comes back, the connections must be reloaded: their status
    // and the actions available depend on it.
    const previousIncidentsKey = useRef(null);
    useEffect(() => {
        if (!isSuccess) return;

        const incidentsKey = incidents
            .map((incident) => `${incident.connector_id}:${incident.health_status}`)
            .sort()
            .join(',');

        if (previousIncidentsKey.current !== null && previousIncidentsKey.current !== incidentsKey) {
            queryClient.invalidateQueries({ queryKey: ['providers-connections'] });
        }

        previousIncidentsKey.current = incidentsKey;
    }, [incidents, isSuccess, queryClient]);

    const outages = useMemo(() => {
        const connectorsIds = new Set(
            validProvidersConnections
                .filter((connection) => connection.connector?.service !== 'manual')
                .map((connection) => connection.provider_connector_id),
        );

        return incidents
            .filter((incident) => incident.health_status === CONNECTOR_HEALTH_STATUSES.BROKEN)
            .filter((incident) => connectorsIds.has(incident.connector_id))
            .filter((incident) => !dismissed.includes(getIncidentKey(incident)));
    }, [incidents, validProvidersConnections, dismissed]);

    if (outages.length === 0) return null;

    const handleDismiss = () => {
        const newDismissed = [...dismissed, ...outages.map(getIncidentKey)];

        storage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(newDismissed));
        setDismissed(newDismissed);
    };

    const [firstOutage] = outages;

    const { title, description } = getOutageTexts(
        outages.map((outage) => ({
            name: outage.name,
            service: outage.service,
            message: outage.health_status_message,
            since: outage.health_status_since,
        })),
    );

    return (
        <Alert
            title={title}
            description={description}
            onClose={handleDismiss}
            actions={[
                <Button key="help" href={helpLink} target="_blank">
                    <OutlineDocs size={18} />
                    {helpLabel}
                </Button>,
            ]}
            icon={
                <div className="relative">
                    <img src={firstOutage.logo_url} alt={firstOutage.name} className="w-full h-auto" />
                    <div className="absolute size-4 bg-gray-400 rounded-full right-[-3px] top-[-3px] border-2 border-white" />
                </div>
            }
        />
    );
}
